import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { toastify } from '../../../../../../utils/toast';
import { customer_account_base_url } from '../../../../../../utils/urls';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const CATEGORIES = [
    { key: 'unit', label: 'Residential Meters', color: [30, 64, 175], hex: '#1e40af' },
    { key: 'floor', label: 'Floor Meters', color: [2, 132, 199], hex: '#0284c7' },
    { key: 'bulk', label: 'Bulk Meters', color: [101, 65, 168], hex: '#6541a8' },
];

const getCategoryConfig = (key) =>
    CATEGORIES.find((c) => c.key === key) || { key, label: `${key} Meters`, color: [100, 100, 100], hex: '#646464' };

const groupByCategory = (rows) =>
    CATEGORIES.reduce((acc, cat) => {
        acc[cat.key] = rows.filter((r) => (r.meterCategory || 'unit') === cat.key);
        return acc;
    }, {});

const CustomersTab = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const [rows, setRows] = useState(10);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFilter, setExportFilter] = useState('all');
    const [exportFormat, setExportFormat] = useState('pdf');

    const [showAssignmentReportModal, setShowAssignmentReportModal] = useState(false);
    const [assignmentReportFormat, setAssignmentReportFormat] = useState('pdf');
    const [assignmentReportLoading, setAssignmentReportLoading] = useState(false);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);

    const [showConsumptionModal, setShowConsumptionModal] = useState(false);
    const [consumptionDate, setConsumptionDate] = useState(new Date());
    const [consumptionFormat, setConsumptionFormat] = useState('pdf');
    const [consumptionLoading, setConsumptionLoading] = useState(false);

    const [showStatementModal, setShowStatementModal] = useState(false);
    const [statementDate, setStatementDate] = useState(new Date());
    const [statementFormat, setStatementFormat] = useState('pdf');
    const [statementLoading, setStatementLoading] = useState(false);
    const [showSummaryStatementModal, setShowSummaryStatementModal] = useState(false);
    const [summaryMonthsSelected, setSummaryMonthsSelected] = useState([]);
    const [summaryFormat, setSummaryFormat] = useState('pdf');
    const [summaryLoading, setSummaryLoading] = useState(false);

    const rowsPerPageOptions = [10, 20, 50, 100];

    const maskName = (fullName) => {
        if (!fullName || fullName === 'N/A') return fullName;
        const parts = fullName.trim().split(' ');
        return parts
            .map((part, index) => {
                if (index === 0) return part;
                if (part.length <= 1) return part;
                return part[0] + '*'.repeat(part.length - 1);
            })
            .join(' ');
    };

    const maskPhone = (phone) => {
        if (!phone || phone === 'N/A') return phone;
        const cleaned = phone.replace(/\s+/g, '');
        if (cleaned.length < 7) return phone;
        const visibleStart = cleaned.slice(0, 4);
        const visibleEnd = cleaned.slice(-3);
        const maskedMiddle = '*'.repeat(cleaned.length - 7);
        return `${visibleStart}${maskedMiddle}${visibleEnd}`;
    };

    useEffect(() => {
        if (selectedFacilityId) fetchAccounts(selectedFacilityId);
    }, [selectedFacilityId]);

    const fetchAccounts = async (facilityId) => {
        try {
            setLoading(true);
            const idToUse = facilityId || selectedFacilityId;
            if (!idToUse) throw new Error('No facility ID available');

            const response = await makeRequest2(
                `${customer_account_base_url}/all/${idToUse}`,
                'GET',
                null
            );

            if (response.success) {
                const accountsData = Array.isArray(response.data.data) ? response.data.data : [];

                const transformedData = accountsData.map((account) => ({
                    id: account._id || account.id,
                    CustomerInfo: { fullName: account.customerName },
                    meterNumber: account.meterNumber || 'N/A',
                    UnitInfo: { name: account.unitName || 'N/A' },
                    accountNumber: account.account_no,
                    phoneNumber: account.phoneNumber || 'N/A',
                    userType: account.payment_type,
                    accountBalance: 0,
                    negativeBalance: 0,
                    currentReading: account.currentReading,
                    status: account.status,
                    meterCategory: account.meterCategory || 'unit',
                    originalData: account,
                    balanceLoaded: false,
                }));
                setAccounts(transformedData);
                await fetchBalances(transformedData);
            } else {
                toastify('Failed to fetch customers', 'error');
            }
        } catch (error) {
            toastify('Error fetching customers', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Two buckets only: unitMeters (has a unit assigned) and commonArea (no unit id).
    const categorizeMeter = (row) => {
        if (!row.unitName || row.unitName === 'N/A') {
            return 'commonArea';
        }
        return 'unitMeters';
    };

    const fetchBalances = async (accountsList) => {
        try {
            setBalanceLoading(true);
            const balancePromises = accountsList.map(async (account) => {
                try {
                    const response = await makeRequest2(
                        `${customer_account_base_url}/get_accounts_balance/${account.id}`,
                        'GET',
                        null
                    );
                    if (response.success) {
                        const balanceData = response.data.data || {};
                        return {
                            id: account.id,
                            accountBalance: balanceData.currentBalance || 0,
                            negativeBalance: balanceData.negativeBalance || 0,
                            balanceLoaded: true,
                        };
                    }
                    return { id: account.id, accountBalance: 0, negativeBalance: 0, balanceLoaded: true };
                } catch {
                    return { id: account.id, accountBalance: 0, negativeBalance: 0, balanceLoaded: true };
                }
            });

            const balanceResults = await Promise.all(balancePromises);
            setAccounts((prev) =>
                prev.map((account) => {
                    const b = balanceResults.find((r) => r.id === account.id);
                    return b ? { ...account, ...b } : account;
                })
            );
        } catch {
            toastify('Error fetching account balances', 'error');
        } finally {
            setBalanceLoading(false);
        }
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(value ?? 0);

    const fetchMultiMonthSummaryData = async (monthsArray) => {
        try {
            setSummaryLoading(true);

            const summaryData = {};

            for (const yearMonth of monthsArray) {
                try {
                    const consumptionRes = await makeRequest2(
                        `${customer_account_base_url}/monthly-consumption/${selectedFacilityId}/${yearMonth}`,
                        'GET',
                        null
                    );

                    if (!consumptionRes.success) continue;

                    const consumptionRows = consumptionRes.data.data || [];

                    const accountIdMap = {};
                    accounts.forEach((a) => {
                        if (a.accountNumber && a.id) accountIdMap[String(a.accountNumber)] = a.id;
                    });

                    const monthData = {
                        unitMeters: { consumption: 0, paid: 0, count: 0 },
                        commonArea: { consumption: 0, paid: 0, count: 0 },
                    };

                    const BATCH_SIZE = 10;
                    const enriched = consumptionRows.map((row) => ({ ...row, totalPaid: 0 }));

                    for (let i = 0; i < enriched.length; i += BATCH_SIZE) {
                        const batch = enriched.slice(i, i + BATCH_SIZE);
                        await Promise.all(
                            batch.map(async (row) => {
                                const accountId =
                                    row.accountId ||
                                    row._id ||
                                    accountIdMap[String(row.accountNumber)] ||
                                    accountIdMap[String(row.account_no)];
                                if (!accountId) return;
                                try {
                                    const creditsRes = await makeRequest2(
                                        `${customer_account_base_url}/get_accounts_credits/${accountId}`,
                                        'GET',
                                        null
                                    );
                                    if (creditsRes.success) {
                                        const creditList = creditsRes.data.data?.credits || [];
                                        row.totalPaid = creditList
                                            .filter(
                                                (c) =>
                                                    c.type === 'Mobile Money' &&
                                                    typeof c.addedOn === 'string' &&
                                                    c.addedOn.startsWith(yearMonth)
                                            )
                                            .reduce((sum, c) => sum + (c.amount || 0), 0);
                                    }
                                } catch { /* leave totalPaid as 0 */ }
                            })
                        );
                    }

                    enriched.forEach((row) => {
                        const cat = categorizeMeter(row);
                        const consumption = row.consumption || 0;
                        const paid = row.totalPaid || 0;

                        monthData[cat].consumption += consumption;
                        monthData[cat].paid += paid;
                        monthData[cat].count += 1;
                    });

                    summaryData[yearMonth] = monthData;
                } catch (error) {
                    console.error(`Error fetching data for ${yearMonth}:`, error);
                }
            }

            return summaryData;
        } catch (error) {
            toastify('Error fetching summary data', 'error');
            return null;
        } finally {
            setSummaryLoading(false);
        }
    };

    const fetchStatementData = async (yearMonth) => {
        try {
            setStatementLoading(true);
            const consumptionRes = await makeRequest2(
                `${customer_account_base_url}/monthly-consumption/${selectedFacilityId}/${yearMonth}`,
                'GET',
                null
            );
            const consumptionRows = consumptionRes.success ? (consumptionRes.data.data || []) : [];

            if (consumptionRows.length === 0) {
                toastify('No consumption data found for the selected month', 'warning');
                return null;
            }

            const accountIdMap = {};
            accounts.forEach((a) => {
                if (a.accountNumber && a.id) accountIdMap[String(a.accountNumber)] = a.id;
            });

            const BATCH_SIZE = 10;
            const enriched = consumptionRows.map((row) => ({ ...row, totalPaid: 0 }));

            for (let i = 0; i < enriched.length; i += BATCH_SIZE) {
                const batch = enriched.slice(i, i + BATCH_SIZE);
                await Promise.all(
                    batch.map(async (row) => {
                        const accountId =
                            row.accountId ||
                            row._id ||
                            accountIdMap[String(row.accountNumber)] ||
                            accountIdMap[String(row.account_no)];
                        if (!accountId) return;
                        try {
                            const creditsRes = await makeRequest2(
                                `${customer_account_base_url}/get_accounts_credits/${accountId}`,
                                'GET',
                                null
                            );
                            if (creditsRes.success) {
                                const creditList = creditsRes.data.data?.credits || [];
                                row.totalPaid = creditList
                                    .filter(
                                        (c) =>
                                            c.type === 'Mobile Money' &&
                                            typeof c.addedOn === 'string' &&
                                            c.addedOn.startsWith(yearMonth)
                                    )
                                    .reduce((sum, c) => sum + (c.amount || 0), 0);
                            }
                        } catch { /* leave totalPaid as 0 */ }
                    })
                );
            }
            return enriched;
        } catch (error) {
            toastify('Error fetching statement data', 'error');
            return null;
        } finally {
            setStatementLoading(false);
        }
    };

    const fetchConsumptionData = async (yearMonth) => {
        try {
            setConsumptionLoading(true);
            const response = await makeRequest2(
                `${customer_account_base_url}/monthly-consumption/${selectedFacilityId}/${yearMonth}`,
                'GET',
                null
            );
            if (response.success) return response.data.data || [];
            toastify('Failed to fetch consumption data', 'error');
            return [];
        } catch {
            toastify('Error fetching consumption data', 'error');
            return [];
        } finally {
            setConsumptionLoading(false);
        }
    };

    const fetchAssignmentReportData = async () => {
        try {
            setAssignmentReportLoading(true);
            const response = await makeRequest2(
                `${customer_account_base_url}/customer-assignment-report/${selectedFacilityId}`,
                'GET',
                null
            );
            if (response.success) {
                const rows = response.data.data || [];
                if (rows.length === 0) {
                    toastify('No customer-assigned meters found', 'warning');
                    return null;
                }
                return rows;
            }
            toastify('Failed to fetch assignment report', 'error');
            return null;
        } catch {
            toastify('Error fetching assignment report', 'error');
            return null;
        } finally {
            setAssignmentReportLoading(false);
        }
    };

    const handleExportAssignmentReportPDF = async () => {
        const data = await fetchAssignmentReportData();
        if (!data) return;

        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('METER CUSTOMER ASSIGNMENT REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Consumption before vs after customer assignment', pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Total Meters: ${data.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Customer names are partially masked for privacy.', 14, 54);

        const tableRows = data.map((row, i) => [
            i + 1,
            maskName(row.customerName) || 'N/A',
            row.unitName || 'N/A',
            row.meterNumber || 'N/A',
            row.consumptionBeforeAssignment !== null ? row.consumptionBeforeAssignment.toFixed(2) : 'N/A',
            row.consumptionAfterAssignment !== null ? row.consumptionAfterAssignment.toFixed(2) : 'N/A',
            row.totalConsumption !== null ? row.totalConsumption.toFixed(2) : 'N/A',
            formatCurrency(row.totalPaid || 0),
            row.billingStartDate || 'Not billed yet',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['#', 'Customer', 'Unit', 'Meter No.', 'Before Assign (m³)', 'After Assign (m³)', 'Total (m³)', 'Total Paid', 'Billing Start']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: {
                4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' },
                7: { halign: 'right' },
            },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Meter_Assignment_Report_${timestamp}.pdf`);
        setShowAssignmentReportModal(false);
        toastify('Assignment report exported successfully', 'success');
    };

    const handleExportAssignmentReportExcel = async () => {
        const data = await fetchAssignmentReportData();
        if (!data) return;

        const excelData = data.map((row, i) => ({
            'No.': i + 1,
            'Customer Name': maskName(row.customerName) || 'N/A',
            'Unit / Floor': row.unitName || 'N/A',
            'Meter Number': row.meterNumber || 'N/A',
            'Account Number': row.accountNumber || 'N/A',
            'Initial Reading (m³)': row.initialReading,
            'Reading at Assignment (m³)': row.readingAtAssignment ?? 'N/A',
            'Current Reading (m³)': row.currentReading,
            'Consumption Before Assignment (m³)': row.consumptionBeforeAssignment ?? 'N/A',
            'Consumption After Assignment (m³)': row.consumptionAfterAssignment ?? 'N/A',
            'Total Consumption (m³)': row.totalConsumption ?? 'N/A',
            'Total Paid (KES)': row.totalPaid || 0,
            'Billing Start Date': row.billingStartDate || 'Not billed yet',
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Assignment Report');

        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Meter_Assignment_Report_${timestamp}.xlsx`);
        setShowAssignmentReportModal(false);
        toastify('Assignment report exported successfully', 'success');
    };

    const handleExportAssignmentReport = () => {
        if (assignmentReportFormat === 'pdf') handleExportAssignmentReportPDF();
        else handleExportAssignmentReportExcel();
    };

    const addCategorySection = (doc, { catLabel, catColor, columns, tableRows, startY, pageWidth, pageHeight, darkGray, lightGray }) => {
        const MARGIN = 14;

        if (startY > pageHeight - 40) {
            doc.addPage();
            startY = 20;
        }

        doc.setFillColor(...catColor);
        doc.roundedRect(MARGIN, startY, pageWidth - MARGIN * 2, 9, 1.5, 1.5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`${catLabel}  (${tableRows.length} record${tableRows.length !== 1 ? 's' : ''})`, MARGIN + 3, startY + 6.2);

        let finalY = startY + 9;

        if (tableRows.length === 0) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(150, 150, 150);
            doc.text('No records for this category.', MARGIN + 4, finalY + 6);
            return finalY + 12;
        }

        doc.autoTable({
            startY: finalY,
            head: [columns.map((c) => c.header)],
            body: tableRows,
            theme: 'striped',
            headStyles: {
                fillColor: catColor.map((v) => Math.min(v + 40, 255)),
                textColor: 255,
                fontSize: 8,
                fontStyle: 'bold',
                halign: 'center',
            },
            bodyStyles: { fontSize: 7.5, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: columns.reduce((acc, col, i) => {
                acc[i] = col.style || {};
                return acc;
            }, {}),
            margin: { left: MARGIN, right: MARGIN },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 20, { align: 'center' });
                doc.setFont('helvetica', 'normal');
                doc.text('Developed and Maintained by Payserve', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        return doc.lastAutoTable.finalY + 6;
    };

    const handleExportStatementPDF = async () => {
        const yearMonth = `${statementDate.getFullYear()}-${String(statementDate.getMonth() + 1).padStart(2, '0')}`;
        const data = await fetchStatementData(yearMonth);
        if (!data) return;

        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];
        const successColor = [22, 163, 74];

        const monthLabel = new Date(statementDate.getFullYear(), statementDate.getMonth(), 1)
            .toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('MONTHLY CUSTOMER STATEMENT', pageWidth / 2, 17, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${monthLabel}`, pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 53);
        doc.text(`Total Accounts: ${data.length}`, pageWidth - 14, 53, { align: 'right' });

        const totalConsumption = data.reduce((s, r) => s + (r.consumption || 0), 0);
        const totalPaid = data.reduce((s, r) => s + (r.totalPaid || 0), 0);
        const summaryY = 57;
        doc.setFillColor(235, 245, 255);
        doc.roundedRect(14, summaryY, pageWidth - 28, 12, 2, 2, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(
            `Total Consumption: ${totalConsumption.toFixed(2)} m³   |   Total Amount Paid: ${formatCurrency(totalPaid)}`,
            pageWidth / 2, summaryY + 7.5, { align: 'center' }
        );

        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(130, 130, 130);
        doc.text('* Names and phone numbers are partially masked for privacy.', 14, summaryY + 18);

        const grouped = groupByCategory(data);
        const stmtColumns = [
            { header: '#', style: { cellWidth: 8, halign: 'center' } },
            { header: 'Customer', style: { cellWidth: 42 } },
            { header: 'Unit / Floor', style: { cellWidth: 28 } },
            { header: 'Account No.', style: { cellWidth: 30 } },
            { header: 'Phone', style: { cellWidth: 26 } },
            { header: 'Consumed (m³)', style: { cellWidth: 24, halign: 'right', fontStyle: 'bold' } },
            { header: 'Amt Paid', style: { cellWidth: 30, halign: 'right', fontStyle: 'bold', textColor: successColor } },
        ];

        let curY = summaryY + 22;
        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const catConsumption = catRows.reduce((s, r) => s + (r.consumption || 0), 0);
            const catPaid = catRows.reduce((s, r) => s + (r.totalPaid || 0), 0);

            const tableRows = catRows.map((row, i) => [
                i + 1,
                maskName(row.customerName) || 'N/A',
                row.unitName || 'N/A',
                row.accountNumber || 'N/A',
                maskPhone(row.phoneNumber) || 'N/A',
                (row.consumption || 0).toFixed(2),
                formatCurrency(row.totalPaid || 0),
            ]);

            if (catRows.length > 0) {
                tableRows.push(['', 'SUBTOTAL', '', '', '', catConsumption.toFixed(2), formatCurrency(catPaid)]);
            }

            curY = addCategorySection(doc, {
                catLabel: cat.label,
                catColor: cat.color,
                columns: stmtColumns,
                tableRows,
                startY: curY,
                pageWidth,
                pageHeight,
                darkGray,
                lightGray,
            });
        }

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Customer_Statement_${yearMonth}_${timestamp}.pdf`);
        setShowStatementModal(false);
        toastify('Statement exported successfully', 'success');
    };

    const handleExportStatementExcel = async () => {
        const yearMonth = `${statementDate.getFullYear()}-${String(statementDate.getMonth() + 1).padStart(2, '0')}`;
        const data = await fetchStatementData(yearMonth);
        if (!data) return;

        const monthLabel = new Date(statementDate.getFullYear(), statementDate.getMonth(), 1)
            .toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

        const wb = XLSX.utils.book_new();
        const grouped = groupByCategory(data);

        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const excelData = catRows.map((row, i) => ({
                'No.': i + 1,
                'Customer Name': maskName(row.customerName) || 'N/A',
                'Unit / Floor': row.unitName || 'N/A',
                'Account Number': row.accountNumber || 'N/A',
                'Phone Number': maskPhone(row.phoneNumber) || 'N/A',
                'Consumption (m³)': parseFloat((row.consumption || 0).toFixed(2)),
                'Amount Paid (KES)': parseFloat((row.totalPaid || 0).toFixed(2)),
            }));

            if (catRows.length > 0) {
                const catConsumption = catRows.reduce((s, r) => s + (r.consumption || 0), 0);
                const catPaid = catRows.reduce((s, r) => s + (r.totalPaid || 0), 0);
                excelData.push({
                    'No.': '', 'Customer Name': 'SUBTOTAL', 'Unit / Floor': '', 'Account Number': '',
                    'Phone Number': '',
                    'Consumption (m³)': parseFloat(catConsumption.toFixed(2)),
                    'Amount Paid (KES)': parseFloat(catPaid.toFixed(2)),
                });
            }

            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 20 }];
            XLSX.utils.book_append_sheet(wb, ws, cat.label);
        }

        const totalConsumption = data.reduce((s, r) => s + (r.consumption || 0), 0);
        const totalPaid = data.reduce((s, r) => s + (r.totalPaid || 0), 0);
        const summaryData = [
            { 'Category': 'Residential Meters', 'Records': (grouped.unit || []).length, 'Consumption (m³)': (grouped.unit || []).reduce((s, r) => s + (r.consumption || 0), 0).toFixed(2), 'Amount Paid (KES)': (grouped.unit || []).reduce((s, r) => s + (r.totalPaid || 0), 0).toFixed(2) },
            { 'Category': 'Floor Meters', 'Records': (grouped.floor || []).length, 'Consumption (m³)': (grouped.floor || []).reduce((s, r) => s + (r.consumption || 0), 0).toFixed(2), 'Amount Paid (KES)': (grouped.floor || []).reduce((s, r) => s + (r.totalPaid || 0), 0).toFixed(2) },
            { 'Category': 'Bulk Meters', 'Records': (grouped.bulk || []).length, 'Consumption (m³)': (grouped.bulk || []).reduce((s, r) => s + (r.consumption || 0), 0).toFixed(2), 'Amount Paid (KES)': (grouped.bulk || []).reduce((s, r) => s + (r.totalPaid || 0), 0).toFixed(2) },
            { 'Category': 'TOTAL', 'Records': data.length, 'Consumption (m³)': totalConsumption.toFixed(2), 'Amount Paid (KES)': totalPaid.toFixed(2) },
        ];
        const infoData = [
            { Info: 'Monthly Customer Statement Report' },
            { Info: `Period: ${monthLabel}` },
            { Info: `Generated: ${new Date().toLocaleString()}` },
            { Info: '* Names and phone numbers are partially masked for privacy.' },
        ];
        const summaryWs = XLSX.utils.json_to_sheet(summaryData);
        const infoWs = XLSX.utils.json_to_sheet(infoData);
        XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
        XLSX.utils.book_append_sheet(wb, infoWs, 'Report Info');

        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Customer_Statement_${yearMonth}_${timestamp}.xlsx`);
        setShowStatementModal(false);
        toastify('Statement exported successfully', 'success');
    };

    const handleExportStatement = () => {
        if (statementFormat === 'pdf') handleExportStatementPDF();
        else handleExportStatementExcel();
    };

    const handleExportConsumptionPDF = async () => {
        const yearMonth = `${consumptionDate.getFullYear()}-${String(consumptionDate.getMonth() + 1).padStart(2, '0')}`;
        const consumptionData = await fetchConsumptionData(yearMonth);
        if (!consumptionData.length) { toastify('No consumption data found for the selected period', 'warning'); return; }

        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [33, 150, 243];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        const monthYear = new Date(consumptionDate.getFullYear(), consumptionDate.getMonth())
            .toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('MONTHLY WATER CONSUMPTION REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${monthYear}`, pageWidth / 2, 28, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(9);
        doc.text(`Generated: ${generatedDate}`, 14, 53);
        doc.text(`Total Meters: ${consumptionData.length}`, pageWidth - 14, 53, { align: 'right' });

        const totalConsumption = consumptionData.reduce((s, r) => s + (r.consumption || 0), 0);
        doc.text(`Total Consumption: ${totalConsumption.toFixed(2)} m³`, pageWidth / 2, 59, { align: 'center' });
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.setFont('helvetica', 'italic');
        doc.text('* Names and phone numbers are partially masked for privacy.', 14, 65);

        const consColumns = [
            { header: '#', style: { cellWidth: 8, halign: 'center' } },
            { header: 'Customer', style: { cellWidth: 42 } },
            { header: 'Unit/Floor', style: { cellWidth: 28 } },
            { header: 'Phone', style: { cellWidth: 28 } },
            { header: 'Meter No.', style: { cellWidth: 24 } },
            { header: 'Initial', style: { cellWidth: 18, halign: 'right' } },
            { header: 'Final', style: { cellWidth: 18, halign: 'right' } },
            { header: 'Usage (m³)', style: { cellWidth: 20, halign: 'right', fontStyle: 'bold' } },
        ];

        const grouped = groupByCategory(consumptionData);
        let curY = 70;

        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const tableRows = catRows.map((item, i) => [
                i + 1,
                maskName(item.customerName) || 'N/A',
                item.unitName || 'N/A',
                maskPhone(item.phoneNumber) || 'N/A',
                item.meterNumber || 'N/A',
                (item.initialReading || 0).toFixed(2),
                (item.finalReading || 0).toFixed(2),
                (item.consumption || 0).toFixed(2),
            ]);

            if (catRows.length > 0) {
                const catTotal = catRows.reduce((s, r) => s + (r.consumption || 0), 0);
                tableRows.push(['', 'SUBTOTAL', '', '', '', '', '', catTotal.toFixed(2)]);
            }

            curY = addCategorySection(doc, {
                catLabel: cat.label,
                catColor: cat.color,
                columns: consColumns,
                tableRows,
                startY: curY,
                pageWidth,
                pageHeight,
                darkGray,
                lightGray,
            });
        }

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Water_Consumption_${yearMonth.replace(/ /g, '_')}_${timestamp}.pdf`);
        setShowConsumptionModal(false);
        toastify('Consumption report exported successfully', 'success');
    };

    const handleExportConsumptionExcel = async () => {
        const yearMonth = `${consumptionDate.getFullYear()}-${String(consumptionDate.getMonth() + 1).padStart(2, '0')}`;
        const consumptionData = await fetchConsumptionData(yearMonth);
        if (!consumptionData.length) { toastify('No consumption data found for the selected period', 'warning'); return; }

        const wb = XLSX.utils.book_new();
        const grouped = groupByCategory(consumptionData);

        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const excelData = catRows.map((item, i) => ({
                'No.': i + 1,
                'Customer Name': maskName(item.customerName) || 'N/A',
                'Unit / Floor': item.unitName || 'N/A',
                'Phone Number': maskPhone(item.phoneNumber) || 'N/A',
                'Meter Number': item.meterNumber || 'N/A',
                'Year-Month': item.yearMonth,
                'Initial Reading (m³)': parseFloat((item.initialReading || 0).toFixed(2)),
                'Final Reading (m³)': parseFloat((item.finalReading || 0).toFixed(2)),
                'Consumption (m³)': parseFloat((item.consumption || 0).toFixed(2)),
                'Has Account': item.hasCustomerAccount ? 'Yes' : 'No',
            }));

            if (catRows.length > 0) {
                const catTotal = catRows.reduce((s, r) => s + (r.consumption || 0), 0);
                excelData.push({
                    'No.': '', 'Customer Name': 'SUBTOTAL', 'Unit / Floor': '', 'Phone Number': '',
                    'Meter Number': '', 'Year-Month': '', 'Initial Reading (m³)': '',
                    'Final Reading (m³)': '', 'Consumption (m³)': parseFloat(catTotal.toFixed(2)), 'Has Account': '',
                });
            }

            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 12 }];
            XLSX.utils.book_append_sheet(wb, ws, cat.label);
        }

        const summaryData = CATEGORIES.map((cat) => {
            const catRows = grouped[cat.key] || [];
            return {
                'Category': cat.label,
                'Meters': catRows.length,
                'Total Consumption (m³)': parseFloat(catRows.reduce((s, r) => s + (r.consumption || 0), 0).toFixed(2)),
            };
        });
        summaryData.push({
            'Category': 'TOTAL',
            'Meters': consumptionData.length,
            'Total Consumption (m³)': parseFloat(consumptionData.reduce((s, r) => s + (r.consumption || 0), 0).toFixed(2)),
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Water_Consumption_${yearMonth}_${timestamp}.xlsx`);
        setShowConsumptionModal(false);
        toastify('Consumption report exported successfully', 'success');
    };

    const handleExportConsumption = () => {
        if (consumptionFormat === 'pdf') handleExportConsumptionPDF();
        else handleExportConsumptionExcel();
    };

    const getFilteredAccountsForExport = () => {
        if (exportFilter === 'all') return accounts;
        return accounts.filter((acc) => acc.status.toLowerCase() === exportFilter);
    };

    const handleExportPDF = () => {
        const dataToExport = getFilteredAccountsForExport();
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text('WATER CUSTOMER ACCOUNTS', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        const filterText = exportFilter === 'all' ? 'All Accounts' : `${exportFilter.charAt(0).toUpperCase() + exportFilter.slice(1)} Accounts`;
        doc.text(filterText, pageWidth / 2, 28, { align: 'center' });

        doc.setTextColor(...darkGray);
        doc.setFontSize(9);
        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Total Accounts: ${dataToExport.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.setFont('helvetica', 'italic');
        doc.text('* Customer names and phone numbers are partially masked for privacy.', 14, 54);

        const acctColumns = [
            { header: '#', style: { cellWidth: 10, halign: 'center' } },
            { header: 'Customer', style: { cellWidth: 48 } },
            { header: 'Unit/Floor', style: { cellWidth: 28 } },
            { header: 'Account No.', style: { cellWidth: 34 } },
            { header: 'Phone', style: { cellWidth: 34 } },
        ];

        const grouped = groupByCategory(dataToExport);
        let curY = 58;

        for (const cat of CATEGORIES) {
            const catRows = (grouped[cat.key] || []).map((account, i) => [
                i + 1,
                maskName(account.CustomerInfo?.fullName) || 'N/A',
                account.UnitInfo?.name || 'N/A',
                account.accountNumber || 'N/A',
                maskPhone(account.phoneNumber) || 'N/A',
            ]);

            curY = addCategorySection(doc, {
                catLabel: cat.label,
                catColor: cat.color,
                columns: acctColumns,
                tableRows: catRows,
                startY: curY,
                pageWidth,
                pageHeight,
                darkGray,
                lightGray,
            });
        }

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Customer_Accounts_${filterText.replace(/ /g, '_')}_${timestamp}.pdf`);
        setShowExportModal(false);
        toastify('PDF exported successfully', 'success');
    };

    const handleExportExcel = () => {
        const dataToExport = getFilteredAccountsForExport();
        const wb = XLSX.utils.book_new();
        const grouped = groupByCategory(dataToExport);

        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const excelData = catRows.map((account, i) => ({
                'No.': i + 1,
                'Customer Name': maskName(account.CustomerInfo?.fullName) || 'N/A',
                'Unit / Floor': account.UnitInfo?.name || 'N/A',
                'Account Number': account.accountNumber || 'N/A',
                'Phone Number': maskPhone(account.phoneNumber) || 'N/A',
                'Meter Number': account.meterNumber || 'N/A',
                'Payment Type': account.userType || 'N/A',
                'Status': account.status || 'N/A',
            }));
            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 14 }, { wch: 12 }];
            XLSX.utils.book_append_sheet(wb, ws, cat.label);
        }

        const summaryData = CATEGORIES.map((cat) => ({
            'Category': cat.label,
            'Total Accounts': (grouped[cat.key] || []).length,
            'Active': (grouped[cat.key] || []).filter((a) => a.status?.toLowerCase() === 'active').length,
            'Inactive': (grouped[cat.key] || []).filter((a) => a.status?.toLowerCase() === 'inactive').length,
        }));
        summaryData.push({ 'Category': 'TOTAL', 'Total Accounts': dataToExport.length, 'Active': dataToExport.filter((a) => a.status?.toLowerCase() === 'active').length, 'Inactive': dataToExport.filter((a) => a.status?.toLowerCase() === 'inactive').length });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

        const filterText = exportFilter === 'all' ? 'All' : exportFilter.charAt(0).toUpperCase() + exportFilter.slice(1);
        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Customer_Accounts_${filterText}_${timestamp}.xlsx`);
        setShowExportModal(false);
        toastify('Excel file exported successfully', 'success');
    };

    const handleExport = () => {
        if (exportFormat === 'pdf') handleExportPDF();
        else handleExportExcel();
    };

    const generateCSVTemplate = () => {
        let csv = "meterNumber,customerName,phoneNumber,payment_type,email\n";
        csv += "MTR001,John Doe,0712345678,Prepaid,johndo@gmail.com\n";
        csv += "MTR002,Jane Smith,0723456789,Postpaid,janesmith@gmail.com\n";
        csv += "MTR003,Peter Kimani,0734567890,Prepaid,peterkimani@gmail.com\n";
        return csv;
    };

    const handleTemplateDownload = () => {
        const blob = new Blob([generateCSVTemplate()], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'customer_accounts_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleExportSummaryStatementPDF = async () => {
        if (summaryMonthsSelected.length === 0) {
            toastify('Please select at least one month', 'warning');
            return;
        }

        const summaryData = await fetchMultiMonthSummaryData(summaryMonthsSelected);
        if (!summaryData) return;

        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('MONTHLY CONSUMPTION SUMMARY REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Multiple Months Summary`, pageWidth / 2, 27, { align: 'center' });

        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Months: ${summaryMonthsSelected.length}`, pageWidth - 14, 48, { align: 'right' });

        const tableRows = [];
        const sortedMonths = [...summaryMonthsSelected].sort();

        sortedMonths.forEach((yearMonth) => {
            const data = summaryData[yearMonth];
            if (!data) return;

            const monthDate = new Date(yearMonth + '-01');
            const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

            const unitConsumption = data.unitMeters.consumption.toFixed(2);
            const commonAreaConsumption = data.commonArea.consumption.toFixed(2);
            const totalConsumption = (data.unitMeters.consumption + data.commonArea.consumption).toFixed(2);
            const totalPaid = (data.unitMeters.paid + data.commonArea.paid).toFixed(2);

            tableRows.push([
                monthLabel,
                unitConsumption,
                commonAreaConsumption,
                totalConsumption,
                formatCurrency(totalPaid),
            ]);
        });

        doc.autoTable({
            startY: 52,
            head: [['Month', 'Residential Meters (m³)', 'Common Area (m³)', 'Total (m³)', 'Total Paid']],
            body: tableRows,
            theme: 'striped',
            headStyles: {
                fillColor: primaryColor,
                textColor: 255,
                fontSize: 8.5,
                fontStyle: 'bold',
                halign: 'center',
            },
            bodyStyles: { fontSize: 8, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: {
                0: { halign: 'left', cellWidth: 35 },
                1: { halign: 'right', cellWidth: 32 },
                2: { halign: 'right', cellWidth: 32 },
                3: { halign: 'right', cellWidth: 28, fontStyle: 'bold' },
                4: { halign: 'right', cellWidth: 35 },
            },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text('Developed and Maintained by Payserve', pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Consumption_Summary_${timestamp}.pdf`);
        setShowSummaryStatementModal(false);
        toastify('Summary report exported successfully', 'success');
    };

    const handleExportSummaryStatementExcel = async () => {
        if (summaryMonthsSelected.length === 0) {
            toastify('Please select at least one month', 'warning');
            return;
        }

        const summaryData = await fetchMultiMonthSummaryData(summaryMonthsSelected);
        if (!summaryData) return;

        const wb = XLSX.utils.book_new();
        const excelData = [];
        const sortedMonths = [...summaryMonthsSelected].sort();

        sortedMonths.forEach((yearMonth) => {
            const data = summaryData[yearMonth];
            if (!data) return;

            const monthDate = new Date(yearMonth + '-01');
            const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            const totalConsumption = data.unitMeters.consumption + data.commonArea.consumption;
            const totalPaid = data.unitMeters.paid + data.commonArea.paid;

            excelData.push({
                'Month': monthLabel,
                'Residential Meters (m³)': parseFloat(data.unitMeters.consumption.toFixed(2)),
                'Common Area (m³)': parseFloat(data.commonArea.consumption.toFixed(2)),
                'Total Consumed (m³)': parseFloat(totalConsumption.toFixed(2)),
                'Total Paid (KES)': parseFloat(totalPaid.toFixed(2)),
            });
        });

        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Summary');

        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Consumption_Summary_${timestamp}.xlsx`);
        setShowSummaryStatementModal(false);
        toastify('Summary report exported successfully', 'success');
    };

    const handleExportSummaryStatement = () => {
        if (summaryFormat === 'pdf') handleExportSummaryStatementPDF();
        else handleExportSummaryStatementExcel();
    };

    const toggleMonth = (yearMonth) => {
        setSummaryMonthsSelected((prev) => {
            if (prev.includes(yearMonth)) {
                return prev.filter((m) => m !== yearMonth);
            } else {
                return [...prev, yearMonth];
            }
        });
    };

    const getAvailableMonths = () => {
        const months = [];
        const today = new Date();

        for (let i = 0; i < 12; i++) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            months.push(yearMonth);
        }

        return months;
    };

    const handleFileChange = (e) => {
        if (e.target.files?.length > 0) setImportFile(e.target.files[0]);
    };

    const handleImportSubmit = async (e) => {
        e.preventDefault();
        if (!importFile) { toastify('Please select a CSV file', 'error'); return; }
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('file', importFile);
            const response = await makeRequest2(`${customer_account_base_url}/import/${selectedFacilityId}`, 'POST', formData);
            if (response.success) {
                toastify('Customer accounts imported successfully', 'success');
                fetchAccounts(selectedFacilityId);
                setShowImportModal(false);
                setImportFile(null);
            } else {
                toastify(response.error || 'Failed to import customer accounts', 'error');
            }
        } catch {
            toastify('Error importing CSV', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleView = (rowData) => {
        const accountId = rowData.id || rowData._id;
        const paymentType = rowData.userType?.toLowerCase();
        if (paymentType === 'prepaid') {
            navigate(
                `/facility/utility_management/meter_management/water_meters/view_meter_tabs/customer_account/view_customer_details1/${accountId}`,
                { state: { accountId } }
            );
        } else if (paymentType === 'postpaid') {
            navigate(
                `/facility/utility_management/meter_management/water_meters/view_customer_account`,
                { state: { accountId } }
            );
        } else {
            toastify('Unable to determine payment type for navigation', 'warning');
        }
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button icon="ti ti-eye" className="p-button-rounded p-button-text" onClick={() => handleView(rowData)} tooltip="View" />
        </div>
    );

    const statusBodyTemplate = (rowData) => {
        const status = rowData.status?.toLowerCase() || 'n/a';
        return (
            <span className={`badge rounded-pill text-bg-${status === 'active' ? 'success' : status === 'inactive' ? 'danger' : 'secondary'}`}>
                {rowData.status ? rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1) : 'N/A'}
            </span>
        );
    };

    const paymentTypeTemplate = (rowData) => {
        const userType = rowData.userType?.toLowerCase() || 'n/a';
        return (
            <span className={`badge rounded-pill text-bg-${userType === 'postpaid' ? 'warning' : userType === 'prepaid' ? 'success' : 'secondary'}`}>
                {rowData.userType ? rowData.userType.charAt(0).toUpperCase() + rowData.userType.slice(1) : 'N/A'}
            </span>
        );
    };

    const categoryBodyTemplate = (rowData) => {
        const cfg = getCategoryConfig(rowData.meterCategory || 'unit');
        return (
            <span className="badge rounded-pill" style={{ backgroundColor: cfg.hex, color: '#fff', fontSize: '0.75rem' }}>
                {cfg.label.replace(' Meters', '')}
            </span>
        );
    };

    const currencyTemplate = (value, isLoading = false) => {
        if (isLoading) {
            return (
                <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading...</span>
                </div>
            );
        }
        return value
            ? new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(value)
            : 'KES 0.00';
    };

    const accountBalanceTemplate = (rowData) => currencyTemplate(rowData.accountBalance, balanceLoading && !rowData.balanceLoaded);
    const negativeBalanceTemplate = (rowData) => currencyTemplate(rowData.negativeBalance, balanceLoading && !rowData.balanceLoaded);

    const refreshBalances = async () => {
        if (accounts.length > 0) {
            await fetchBalances(accounts);
            toastify('Balances refreshed successfully', 'success');
        }
    };

    const filteredAccounts = accounts.filter((account) => {
        const searchLower = searchTerm.toLowerCase();
        return [
            account.CustomerInfo?.fullName,
            account.accountNumber,
            account.UnitInfo?.name,
            account.meterNumber,
            account.phoneNumber,
        ].some((field) => field?.toString().toLowerCase().includes(searchLower));
    });

    return (
        <>
            <div className="card">
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-3 col-xs-12">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-9 col-xs-12 d-flex justify-content-end gap-2 flex-wrap">
                            <button className="btn btn-success" onClick={() => setShowExportModal(true)}>
                                <i className="ti ti-file-export me-1"></i>Export Accounts
                            </button>
                            <button className="btn btn-info" onClick={() => setShowConsumptionModal(true)}>
                                <i className="ti ti-report-analytics me-1"></i>Export Consumption
                            </button>

                            <button className="btn btn-secondary" onClick={() => setShowSummaryStatementModal(true)}>
                                <i className="ti ti-chart-bar me-1"></i>Summary Statement
                            </button>
                            <button className="btn btn-warning" onClick={() => { setStatementDate(new Date()); setStatementFormat('pdf'); setShowStatementModal(true); }}>
                                <i className="ti ti-file-invoice me-1"></i>Export Statement
                            </button>
                            <button className="btn btn-primary" onClick={() => setShowImportModal(true)}>
                                <i className="ti ti-upload me-1"></i>Import Customers
                            </button>
                            <button className="btn btn-outline-primary" onClick={refreshBalances} disabled={balanceLoading}>
                                <i className="ti ti-refresh me-1"></i>
                                {balanceLoading ? 'Refreshing...' : 'Refresh Balances'}
                            </button>
                            <button className="btn btn-dark" onClick={() => setShowAssignmentReportModal(true)}>
                                <i className="ti ti-report-money me-1"></i>Assignment Report
                            </button>
                        </div>
                    </div>

                    <div className="dt-responsive table-responsive">
                        <DataTable
                            value={filteredAccounts}
                            emptyMessage="No customers found."
                            sortMode="multiple"
                            paginator
                            rows={rows}
                            rowsPerPageOptions={rowsPerPageOptions}
                            stripedRows
                            loading={loading}
                            tableStyle={{ minWidth: '50rem' }}
                        >
                            <Column field="fullname" header="Customer" sortable body={(rowData) => rowData.CustomerInfo?.fullName || 'N/A'} />
                            <Column field="meterNumber" header="Meter S/N" sortable />
                            <Column field="UnitInfo" header="Unit" sortable body={(rowData) => rowData.UnitInfo?.name || 'N/A'} />
                            <Column field="accountNumber" header="Account No" sortable />
                            <Column field="phoneNumber" header="Phone Number" sortable />
                            <Column field="meterCategory" header="Category" sortable body={categoryBodyTemplate} />
                            <Column field="userType" header="Payment Type" sortable body={paymentTypeTemplate} />
                            <Column field="accountBalance" header="Account Bal" sortable body={accountBalanceTemplate} />
                            <Column field="negativeBalance" header="Negative Bal" sortable body={negativeBalanceTemplate} />
                            <Column field="status" header="Status" body={statusBodyTemplate} sortable />
                            <Column header="Actions" body={actionBodyTemplate} />
                        </DataTable>
                    </div>
                </div>
            </div>

            {showStatementModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#92400e' }}>
                                <h5 className="modal-title text-white">
                                    <i className="ti ti-file-invoice me-2"></i>Export Monthly Customer Statement
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowStatementModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Select Month <span className="text-danger">*</span></label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={`${statementDate.getFullYear()}-${String(statementDate.getMonth() + 1).padStart(2, '0')}`}
                                        onChange={(e) => {
                                            const [y, m] = e.target.value.split('-');
                                            setStatementDate(new Date(y, m - 1, 1));
                                        }}
                                        max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                                    />
                                    <small className="text-muted">Statement will cover the full selected month</small>
                                </div>
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-4">
                                        {['pdf', 'excel'].map((fmt) => (
                                            <div className="form-check" key={fmt}>
                                                <input className="form-check-input" type="radio" name="statementFormat" id={`stFmt_${fmt}`} value={fmt} checked={statementFormat === fmt} onChange={(e) => setStatementFormat(e.target.value)} />
                                                <label className="form-check-label" htmlFor={`stFmt_${fmt}`}>
                                                    <i className={`ti ti-file-type-${fmt === 'pdf' ? 'pdf text-danger' : 'xls text-success'} me-1`}></i>
                                                    {fmt === 'pdf' ? 'PDF Report' : 'Excel Spreadsheet'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    <div>
                                        <strong>Report is separated by meter category:</strong>
                                        <ul className="mb-0 mt-1">
                                            {CATEGORIES.map((c) => (
                                                <li key={c.key}><span className="badge me-1" style={{ backgroundColor: c.hex }}>{c.label}</span></li>
                                            ))}
                                        </ul>
                                        Each section includes consumption (m³) and amount paid for the month.
                                    </div>
                                </div>
                                <div className="alert alert-warning mb-0">
                                    <i className="ti ti-shield-lock me-2"></i>
                                    <strong>Privacy Notice:</strong> Names and phone numbers will be <strong>partially masked</strong>.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowStatementModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-warning" onClick={handleExportStatement} disabled={statementLoading} style={{ color: '#fff' }}>
                                    {statementLoading
                                        ? <><span className="spinner-border spinner-border-sm me-1" />Fetching data...</>
                                        : <><i className="ti ti-download me-1"></i>Export {statementFormat === 'pdf' ? 'PDF' : 'Excel'}</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showExportModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Export Customer Accounts</h5>
                                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-3">
                                        {['pdf', 'excel'].map((fmt) => (
                                            <div className="form-check" key={fmt}>
                                                <input className="form-check-input" type="radio" name="exportFormat" id={`expFmt_${fmt}`} value={fmt} checked={exportFormat === fmt} onChange={(e) => setExportFormat(e.target.value)} />
                                                <label className="form-check-label" htmlFor={`expFmt_${fmt}`}>
                                                    <i className={`ti ti-file-type-${fmt === 'pdf' ? 'pdf text-danger' : 'xls text-success'} me-1`}></i>
                                                    {fmt === 'pdf' ? 'PDF Document' : 'Excel Spreadsheet'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Filter Accounts</label>
                                    <select className="form-select" value={exportFilter} onChange={(e) => setExportFilter(e.target.value)}>
                                        <option value="all">All Accounts ({accounts.length})</option>
                                        <option value="active">Active ({accounts.filter((a) => a.status?.toLowerCase() === 'active').length})</option>
                                        <option value="inactive">Inactive ({accounts.filter((a) => a.status?.toLowerCase() === 'inactive').length})</option>
                                    </select>
                                </div>
                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    Output is <strong>separated by meter category</strong> — Unit, Floor and Bulk each get their own section (PDF) or sheet (Excel).
                                </div>
                                <div className="alert alert-warning mb-0">
                                    <i className="ti ti-shield-lock me-2"></i>
                                    <strong>Privacy Notice:</strong> Names and phone numbers will be <strong>partially masked</strong>.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowExportModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleExport}>
                                    <i className="ti ti-download me-1"></i>Export {exportFormat === 'pdf' ? 'PDF' : 'Excel'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showConsumptionModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title"><i className="ti ti-report-analytics me-2"></i>Export Monthly Water Consumption</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowConsumptionModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Select Month and Year <span className="text-danger">*</span></label>
                                    <input
                                        type="month"
                                        className="form-control"
                                        value={`${consumptionDate.getFullYear()}-${String(consumptionDate.getMonth() + 1).padStart(2, '0')}`}
                                        onChange={(e) => {
                                            const [y, m] = e.target.value.split('-');
                                            setConsumptionDate(new Date(y, m - 1, 1));
                                        }}
                                        max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-3">
                                        {['pdf', 'excel'].map((fmt) => (
                                            <div className="form-check" key={fmt}>
                                                <input className="form-check-input" type="radio" name="consumptionFormat" id={`consFmt_${fmt}`} value={fmt} checked={consumptionFormat === fmt} onChange={(e) => setConsumptionFormat(e.target.value)} />
                                                <label className="form-check-label" htmlFor={`consFmt_${fmt}`}>
                                                    <i className={`ti ti-file-type-${fmt === 'pdf' ? 'pdf text-danger' : 'xls text-success'} me-1`}></i>
                                                    {fmt === 'pdf' ? 'PDF Report' : 'Excel Spreadsheet'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    Output is <strong>separated by meter category</strong> — Unit, Floor and Bulk each get their own section (PDF) or sheet (Excel), each with a subtotal row.
                                </div>
                                <div className="alert alert-warning mb-0">
                                    <i className="ti ti-shield-lock me-2"></i>
                                    <strong>Privacy Notice:</strong> Names and phone numbers will be <strong>partially masked</strong>.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowConsumptionModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-info" onClick={handleExportConsumption} disabled={consumptionLoading}>
                                    {consumptionLoading
                                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</>
                                        : <><i className="ti ti-download me-1"></i>Export {consumptionFormat === 'pdf' ? 'PDF' : 'Excel'}</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAssignmentReportModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#1f2937' }}>
                                <h5 className="modal-title text-white">
                                    <i className="ti ti-report-money me-2"></i>Meter Customer Assignment Report
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowAssignmentReportModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-4">
                                        {['pdf', 'excel'].map((fmt) => (
                                            <div className="form-check" key={fmt}>
                                                <input className="form-check-input" type="radio" name="assignmentReportFormat" id={`arFmt_${fmt}`} value={fmt} checked={assignmentReportFormat === fmt} onChange={(e) => setAssignmentReportFormat(e.target.value)} />
                                                <label className="form-check-label" htmlFor={`arFmt_${fmt}`}>
                                                    <i className={`ti ti-file-type-${fmt === 'pdf' ? 'pdf text-danger' : 'xls text-success'} me-1`}></i>
                                                    {fmt === 'pdf' ? 'PDF Report' : 'Excel Spreadsheet'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    Only meters with a customer assigned (<code>customerId</code> set) are included. For each meter this shows consumption before assignment (from install to first bill), consumption since, and total top-ups on the account.
                                </div>
                                <div className="alert alert-warning mb-0">
                                    <i className="ti ti-shield-lock me-2"></i>
                                    <strong>Privacy Notice:</strong> Customer names will be <strong>partially masked</strong>.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAssignmentReportModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-dark" onClick={handleExportAssignmentReport} disabled={assignmentReportLoading}>
                                    {assignmentReportLoading
                                        ? <><span className="spinner-border spinner-border-sm me-1" />Fetching data...</>
                                        : <><i className="ti ti-download me-1"></i>Export {assignmentReportFormat === 'pdf' ? 'PDF' : 'Excel'}</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showSummaryStatementModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#6c757d' }}>
                                <h5 className="modal-title text-white">
                                    <i className="ti ti-chart-bar me-2"></i>Multi-Month Consumption Summary
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSummaryStatementModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-4">
                                    <label className="form-label fw-bold">Select Months <span className="text-danger">*</span></label>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '15px' }}>
                                        {getAvailableMonths().map((yearMonth) => {
                                            const date = new Date(yearMonth + '-01');
                                            const monthLabel = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                                            const isSelected = summaryMonthsSelected.includes(yearMonth);

                                            return (
                                                <div
                                                    key={yearMonth}
                                                    className={`p-2 border rounded text-center cursor-pointer ${isSelected ? 'bg-primary text-white' : 'bg-light'}`}
                                                    onClick={() => toggleMonth(yearMonth)}
                                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    <strong>{monthLabel}</strong>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <small className="text-muted">
                                        Selected: {summaryMonthsSelected.length} month{summaryMonthsSelected.length !== 1 ? 's' : ''}
                                    </small>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label fw-bold">Export Format</label>
                                    <div className="d-flex gap-4">
                                        {['pdf', 'excel'].map((fmt) => (
                                            <div className="form-check" key={fmt}>
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="summaryFormat"
                                                    id={`sumFmt_${fmt}`}
                                                    value={fmt}
                                                    checked={summaryFormat === fmt}
                                                    onChange={(e) => setSummaryFormat(e.target.value)}
                                                />
                                                <label className="form-check-label" htmlFor={`sumFmt_${fmt}`}>
                                                    <i className={`ti ti-file-type-${fmt === 'pdf' ? 'pdf text-danger' : 'xls text-success'} me-1`}></i>
                                                    {fmt === 'pdf' ? 'PDF Report' : 'Excel Spreadsheet'}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="alert alert-info mb-2">
                                    <i className="ti ti-info-circle me-2"></i>
                                    <div>
                                        <strong>Report shows totals by category:</strong>
                                        <ul className="mb-0 mt-1">
                                            <li><strong>Residential Meters:</strong> Meters with a unit assigned</li>
                                            <li><strong>Common Area:</strong> Meters across the facility but not in units</li>
                                            <li><strong>Total Consumed:</strong> Sum of both</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="alert alert-warning mb-0">
                                    <i className="ti ti-shield-lock me-2"></i>
                                    <strong>Data Accuracy:</strong> Summary reflects consumption recorded for each month period.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowSummaryStatementModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleExportSummaryStatement}
                                    disabled={summaryLoading || summaryMonthsSelected.length === 0}
                                >
                                    {summaryLoading
                                        ? <><span className="spinner-border spinner-border-sm me-1" />Processing...</>
                                        : <><i className="ti ti-download me-1"></i>Export {summaryFormat === 'pdf' ? 'PDF' : 'Excel'}</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showImportModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Import Customer Accounts</h5>
                                <button type="button" className="btn-close" onClick={() => setShowImportModal(false)} />
                            </div>
                            <div className="modal-body">
                                <p>
                                    <button type="button" className="btn btn-link p-0" onClick={handleTemplateDownload}>
                                        Download customer accounts template (.csv)
                                    </button>
                                </p>
                                <div className="alert alert-info">
                                    <strong>Template Info</strong>
                                    <ul className="mb-0">
                                        <li><strong>meterNumber:</strong> Unique meter serial number (Required)</li>
                                        <li><strong>customerName:</strong> Full name of customer (Required)</li>
                                        <li><strong>phoneNumber:</strong> Phone number format 07XXXXXXXX or 01XXXXXXXX (Required)</li>
                                        <li><strong>payment_type:</strong> Either "Prepaid" or "Postpaid" (Required)</li>
                                        <li><strong>email:</strong> Email address (optional)</li>
                                    </ul>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Upload Completed CSV File</label>
                                    <input type="file" accept=".csv" className="form-control" onChange={handleFileChange} />
                                </div>
                                <div className="text-end">
                                    <button type="button" className="btn btn-outline-secondary me-2" onClick={() => setShowImportModal(false)}>Cancel</button>
                                    <button type="button" className="btn btn-primary" onClick={handleImportSubmit} disabled={loading || !importFile}>
                                        {loading && <span className="spinner-border spinner-border-sm me-1"></span>}
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CustomersTab;